// noinspection SpellCheckingInspection

import AddBoxIcon from "@mui/icons-material/AddBox";
import { Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";
import { PageTab } from "app/routes/work/tabs";
import { DialogProps } from "common/components/dialog";
import { NotAuthorized } from "common/components/not-authorized";
import NumberBadge from "common/components/number-badge";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/session/session-context";
import WorkAgreementParameters, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderField,
  WorkAgreementOrderParameters
} from "work/entities/work-agreement/api/request-contracts/work-agreement-parameters";
import {
  addWorkAgreement,
  getActiveWorkAgreementCount,
  getArchivedWorkAgreementCount,
  getErrorLoadingWorkAgreementById,
  getErrorLoadingWorkAgreementsByQuery,
  getIsLoadingWorkAgreementById,
  getIsLoadingWorkAgreementsByQuery,
  getWorkAgreementById,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  loadActiveWorkAgreementCount,
  loadArchivedWorkAgreementCount,
  populateWorkAgreement,
  populateWorkAgreementsByQuery,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementQuery
} from "work/entities/work-agreement/store/work-agreement-redux-slice";
import WorkAgreementDialog from "work/entities/work-agreement/view/work-agreement-dialog";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/entities/work-agreement/work-agreement";

const ToolbarButtonsContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
    },
    display: "flex",
    alignItems: "end",
    flexDirection: "column",
    gap: theme.spacing(1),
  }));
const ToolbarButtons = styled("div")(
  () => ({
    display: "flex",
    alignItems: "end",
    flexWrap: "nowrap",
  }));
const TableTopToolbarContainer = styled("div")(
  () => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }));
const LowerTopToolbarContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "left",
    width: "100%",
  }));
const TableTitle = styled(Typography)(
  ({ theme }) => ({
    paddingLeft: theme.spacing(1),
  }));
const AlertActions = styled("div")(
  ({ theme }) => ({
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "nowrap",
    height: "100%",
    paddingRight: theme.spacing(2),
    whiteSpace: "nowrap",
  }));
const RowTitle = styled(Typography)(
  () => ({
    fontSize: "1.1em",
    fontWeight: "600",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const NewButton = styled(IconButton)(
  () => ({
    padding: 0,
  }));

export default function Work() {
  const columnHelper = createMRTColumnHelper<WorkAgreement>();
  const [activeTab, setActiveTab] = React.useState(PageTab.Active);

  const { numDialogs, openDialog, closeDialog } = useDialog();
  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();  
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const dispatch = useAttorneyHubDispatch();
  const activeWorkAgreementCount = getActiveWorkAgreementCount();
  const archivedWorkAgreementCount = getArchivedWorkAgreementCount();

  const workAgreementQuery = getWorkAgreementQueryByInterface("workAgreementIndex");
  const paginatedWorkAgreements = getWorkAgreementsByQuery(workAgreementQuery);
  const isLoadingQueryFromStore = getIsLoadingWorkAgreementsByQuery(workAgreementQuery);
  const errorLoadingQuery = getErrorLoadingWorkAgreementsByQuery(workAgreementQuery);

  const workAgreementId = routeParams.id ? new Guid(routeParams.id) : undefined;
  const dialogWorkAgreement = getWorkAgreementById(workAgreementId);
  const isLoadingWorkAgreement = getIsLoadingWorkAgreementById(workAgreementId);
  const errorLoadingWorkAgreement = getErrorLoadingWorkAgreementById(workAgreementId);

  const [isLoading, setIsLoading] = React.useState(isLoadingQueryFromStore ?? false);
  const [rowSelection, setRowSelection] = React.useState<
    Record<string, boolean>
  >({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<Array<{ id: string, desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const [rowCount, setRowCount] = useState(0);

  // Handle URL changes
  useEffect(() => {
    if (routeParams.pageTab) {
      setActiveTab(routeParams.pageTab as PageTab);
    } else {
      navigate(`/work/${PageTab.Active}`);
    }
  }, [routeParams]);

  useEffect(
    () => {
      dispatchWorkAgreementQuery();
    },
    [
      activeTab,
      routeParams,
      searchParams
    ]
  );

  useEffect(
    () => {
      const newSearchParams = new URLSearchParams();
      if (globalFilter) {
        newSearchParams.set("query", globalFilter);
      }

      const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
      if (nameFilter) {
        newSearchParams.set("name", nameFilter);
      }

      const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
      if (descriptionFilter) {
        newSearchParams.set("description", descriptionFilter);
      }

      const orderingField = sorting.find((sort) =>
        Object.values(WorkAgreementOrderField)
          .map((field) => field.toLowerCase())
          .includes(sort.id.toLowerCase()));
      
      if (orderingField) {
        newSearchParams.set("orderBy", orderingField.id);
        newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending)
      }       
      
      const paginationPageIndex = pagination.pageIndex ?? 0;
      newSearchParams.set("page", (paginationPageIndex + 1).toString());
      const paginationPageSize = pagination.pageSize ?? 10;
      newSearchParams.set("pageSize", paginationPageSize.toString());      

      setSearchParams(newSearchParams);
    },
    [
      globalFilter,
      columnFilters,
      sorting,
      pagination
    ]
  );  

  useEffect(
    () => {
      if (errorLoadingQuery) {
        enqueueSnackbar(
          "Error loading work agreement table",
          { variant: "error" }
        );
      }
      if (dialogWorkAgreement === null) {
        enqueueSnackbar(
          "Work agreement not found",
          { variant: "warning" }
        );
        navigate(`/work/${activeTab}`);
      } else if (errorLoadingWorkAgreement) {
        enqueueSnackbar(
          "Error loading work agreement",
          { variant: "error" }
        );
        navigate(`/work/${activeTab}`);
      }
    },
    [
      errorLoadingQuery,
      errorLoadingWorkAgreement,
      dialogWorkAgreement
    ]
  );

  useEffect(
    () => {
      if (session.user?.id && workAgreementId && dialogWorkAgreement) {
        const pageTab = routeParams.pageTab as PageTab;
        const action = routeParams.action as string;

        redirectForWorkAgreementIfNecessary(
          action,
          pageTab
        );
      }
    },
    [
      routeParams,
      workAgreementId?.value,
      dialogWorkAgreement?.id?.value
    ]
  );

  useEffect(
    () => {
      if (!session.user) return;
      if (workAgreementId && dialogWorkAgreement === undefined && !isLoadingWorkAgreement && !errorLoadingWorkAgreement) {
        dispatch(
          populateWorkAgreement({
            session: session,
            id: workAgreementId,
          })
        );
      } else if (!(workAgreementId && !dialogWorkAgreement)) {
        const tab =
          WorkAgreementFieldCategory[
          routeParams.tab as keyof typeof WorkAgreementFieldCategory
          ];
        const action = routeParams.action as string;
        if (!action) return;
        if (numDialogs === 0) {
          openWorkAgreementDialog(
            action,
            tab
          );
        }
      }
    },
    [
      workAgreementId?.value,
      dialogWorkAgreement,
      dialogWorkAgreement?.id?.value,
      isLoadingWorkAgreement,
      routeParams
    ]
  );

  useEffect(
    () => {
      if (workAgreementQuery && !isLoadingQueryFromStore && paginatedWorkAgreements === undefined) {
        dispatch(
          populateWorkAgreementsByQuery({
            session: session,
            query: workAgreementQuery,
          })
        );
      }
    },
    [
      workAgreementQuery,
      paginatedWorkAgreements
    ]
  );

  useEffect(() => {
    if (
      workAgreementQuery &&
      !isLoadingQueryFromStore &&
      paginatedWorkAgreements === undefined
    ) {
      dispatch(
        populateWorkAgreementsByQuery({
          session: session,
          query: workAgreementQuery,
        })
      );
    } else if (paginatedWorkAgreements) {
      setRowCount(paginatedWorkAgreements.totalElements);
    }
  }, [workAgreementQuery, paginatedWorkAgreements]);

  useEffect(
    () => {
      setIsLoading(isLoadingQueryFromStore);
    },
    [isLoadingQueryFromStore]
  );

  useEffect(() => {
    if (activeWorkAgreementCount === undefined)
      dispatch(loadActiveWorkAgreementCount({ session: session }));
    if (archivedWorkAgreementCount === undefined)
      dispatch(loadArchivedWorkAgreementCount({ session: session }));
  }, [
    activeWorkAgreementCount,
    archivedWorkAgreementCount
  ]);

  function redirectForWorkAgreementIfNecessary(
    action: string,
    pageTab: PageTab
  ) {
    if (!dialogWorkAgreement) return;
    let redirectAction: string | undefined = undefined;

    if (dialogWorkAgreement.isArchived && action !== "view") {
      redirectAction = "view";
    }

    let redirectPageTab: PageTab | undefined = undefined;

    if (dialogWorkAgreement.isArchived && pageTab !== PageTab.Archived) {
      redirectPageTab = PageTab.Archived;
    }

    if (redirectAction || redirectPageTab) {
      const newPath = `/work/${redirectPageTab ?? pageTab}/${redirectAction ?? action}/${workAgreementId}`;
      navigate(
        newPath,
        { replace: true }
      );
    }
  }

  function openWorkAgreementDialog(action: string, _tab: WorkAgreementFieldCategory) {
    if (!workAgreementId || !session.user || !action) {
      console.warn("Unable to open work agreement dialog. Missing required parameters.");
      return;
    }

    handleOpenWorkAgreementDialog(
      "Work Agreement",
      workAgreementId,
      dialogWorkAgreement?.isArchived
    );
  }

  function handleOpenWorkAgreementDialog(
    title: string,
    workAgreementId: Guid,
    readOnly?: boolean
  ) {
    const parentPath = new URL(
      "..",
      globalThis.location.origin + globalThis.location.pathname
    );

    const dialogProps: DialogProps = {
      title: title,
      titleStyle: { paddingBottom: 0 },
      MuiProps: { fullWidth: false, maxWidth: "xl" },
      contentSxProps: { pb: { xs: 0 } },
      resetStack: true,
      component: (
        <WorkAgreementDialog
          workAgreementId={workAgreementId}
          readOnly={readOnly}
          navigate={navigate}
        />
      ),
      onClose: () => {
        closeDialog();
        navigate(parentPath.pathname);
      },
    }

    openDialog(dialogProps);
  }


  function dispatchWorkAgreementQuery() {
    if (!session.user?.companyEntityId) return;

    const filterParams = new WorkAgreementFilterParameters({
      isArchived: activeTab === PageTab.Archived
    });

    const queryFilter = searchParams.get("query");
    if (queryFilter) {
      filterParams.query = queryFilter;
    }

    const nameFilter = searchParams.get("name");
    if (nameFilter) {
      filterParams.name = nameFilter;
    }

    const descriptionFilter = searchParams.get("description");
    if (descriptionFilter) {
      filterParams.description = descriptionFilter;
    }    

    const orderParams = new WorkAgreementOrderParameters(
      WorkAgreementOrderField.Start,
      OrderDirection.Descending
    );

    const orderByParam = searchParams.get("orderBy");
    const orderDirParam = searchParams.get("orderDir");
    if (orderByParam) {
      const enumValue = Object.values(WorkAgreementOrderField).find(
        (field) => field.toLowerCase() === orderByParam.toLowerCase()
      );
      if (enumValue) {
        orderParams.orderBy = enumValue;
      }
    }
    if (orderDirParam) {
      const enumValue = Object.values(OrderDirection).find(
        (field) => field.toLowerCase() === orderDirParam.toLowerCase()
      );
      if (enumValue) {
        orderParams.orderDir = enumValue;
      }
    }

    let pageIndex = 0;
    let pageSize = 20;
    const pageParam = searchParams.get("page");
    if (pageParam) {
      const parsed = Number.parseInt(pageParam);
      if (!Number.isNaN(parsed) && parsed > 0) {
        pageIndex = parsed - 1;
      }
    }
    const pageSizeParam = searchParams.get("pageSize");
    if (pageSizeParam) {
      const parsed = Number.parseInt(pageSizeParam);
      if (!Number.isNaN(parsed) && parsed > 0 && parsed <= 100) {
        pageSize = parsed;
      }
    }
    const paginationParams = new PaginationParameters(pageIndex, pageSize);

    const workAgreementParams = new WorkAgreementParameters(
      filterParams,
      orderParams
    );

    const newQuery = new WorkAgreementQuery(workAgreementParams, paginationParams);
    dispatch(
      updateWorkAgreementInterfaceQuery({
        interfaceName: "workAgreementIndex",
        query: newQuery,
      })
    );
  }


  function renderNameColumn(work: WorkAgreement) {
    if (!work.name) return <Icon>remove</Icon>
    return <RowTitle>{work.name.value}</RowTitle>;
  }

  function renderDescriptionColumn(work: WorkAgreement) {
    if (!work.description) return <Icon>remove</Icon>
    return work.description.value;
  }

  const columns = [
    columnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name"
    }),
    columnHelper.accessor(renderDescriptionColumn, {
      header: "Description",
      id: "description",
      enableSorting: false,
    }),
    columnHelper.accessor((work) => work.hireDate?.format('MM/DD/YYYY') ?? <Icon>remove</Icon>, {
      header: "Hire Date",
      id: "hireDate",
      enableSorting: false
    }),
    columnHelper.accessor((work) => work.startDate?.format('MM/DD/YYYY') ?? <Icon>remove</Icon>, {
      header: "Start Date",
      id: "start",
    }),
    columnHelper.accessor((work) => work.endDate?.format('MM/DD/YYYY') ?? <Icon>remove</Icon>, {
      header: "End Date",
      id: "end",
    }),
  ];

  if (activeTab === PageTab.Archived) {
    columns.push(
      columnHelper.accessor(
        (work) => work.archiveDate?.format('MM/DD/YYYY') ?? <Icon>remove</Icon>,
        {
          header: "Archive Date",
          id: "archiveDate",
        }
      )
    );
  }

  const getMenuItems = (workAgreement: WorkAgreement): React.ReactNode[] => {
    return [
      <MenuItem
        key="view"
        onClick={() =>
          navigate(`/work/${activeTab}/view/${workAgreement.id}`)
        }
      >
        <ListItemIcon>
          <Icon>visibility</Icon>
        </ListItemIcon>
        <ListItemText primary="View" />
      </MenuItem>,
      <MenuItem
        key="revise"
        disabled={workAgreement.isArchived}
        onClick={() => handleReviseWorkAgreement(workAgreement)}
      >
        <ListItemIcon>
          <Icon>edit</Icon>
        </ListItemIcon>
        <ListItemText primary="Revise" />
      </MenuItem>,
      <MenuItem
        key="cancel"
        disabled={workAgreement.isArchived}
        onClick={() => handleEndWorkAgreement(workAgreement)}
      >
        <ListItemIcon>
          <Icon color="error">cancel</Icon>
        </ListItemIcon>
        <ListItemText primary="End Agreement" />
      </MenuItem>
    ];
  };

  function handleReviseWorkAgreement(workAgreement: WorkAgreement) {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    closeDialog();
    navigate(
      '/proposals/drafts/create',
      {
        state: {
          workAgreement: workAgreement.toJSON(),
        }
      }
    );
  }

  async function handleEndWorkAgreement(workAgreement: WorkAgreement) {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const endResponse = await confirm({
      title: "End Work Agreement?",
      message: "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End"
    });

    if (endResponse === ConfirmResponse.Cancel) return;

    try {
      setIsLoading(true);
      const updatedWorkAgreement = await workAgreement.endAgreement(session.user?.id);
      dispatch(addWorkAgreement(updatedWorkAgreement));
      closeDialog();
      navigate(`/work/archived/view/${updatedWorkAgreement.id?.value}`);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to end work agreement",
        { variant: "error" }
      );
    }
  }

  function openWorkAgreement(workAgreement: WorkAgreement) {
    if (!workAgreement.id) {
      console.error("Work Agreement ID is required");
      return;
    }
    navigate(`/work/${activeTab}/view/${workAgreement.id}`);
  }

  const table = useMaterialReactTable({
    columns,
    data: paginatedWorkAgreements?.data ?? [],
    enableRowSelection: false,
    enableRowActions: true,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => openWorkAgreement(row.original),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              onClick={() => navigate("/proposals/drafts/create")}
            >
              Create Proposal
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate("/proposals/drafts/create")}
          >
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Work</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/work/${newValue}`)}
            >
              <Tab
                value={PageTab.Active}
                label={
                  <NumberBadge
                    tooltip={`${
                      activeWorkAgreementCount ?? 0
                    } active work agreement(s)`}
                    badgeCount={activeWorkAgreementCount ?? 0}
                    color={"info"}
                    text={PageTab.Active}
                  />
                }
              />
              <Tab
                value={PageTab.Archived}
                label={
                  <NumberBadge
                    tooltip={`${
                      archivedWorkAgreementCount ?? 0
                    } archived work agreement(s)`}
                    badgeCount={archivedWorkAgreementCount ?? 0}
                    color={"info"}
                    text={PageTab.Archived}
                  />
                }
              />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    renderRowActionMenuItems: ({ row }) => getMenuItems(row.original),
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: errorLoadingQuery
      ? {
          color: "error",
          children: "Error loading data",
        }
      : {
          action: <AlertActions></AlertActions>,
        },
    rowCount: rowCount,
    pageCount: Math.ceil(
      (paginatedWorkAgreements?.totalElements ?? 0) / pagination.pageSize
    ),
    enablePagination: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: Boolean(errorLoadingQuery),
      showProgressBars: isLoading,
      sorting,
      rowSelection,
    },
  });

  if (!session.user?.companyEntityId) return <NotAuthorized />;

  return (
    <MaterialReactTable table={table} />
  );
}
